import { useState } from 'react'
import { getElplanChangedStarts } from './changes'
import { applyElplanSlotEdits, flattenElplanDraftToHours, groupElplanDraftByHour } from './draft'
import type {
  ElplanDraft,
  ElplanEditorSession,
  ElplanModeSwitchOutcome,
  ElplanSlotEdit,
  ElplanSlotFaults,
  ElplanSlotUnit,
} from './ElplanEditor.types'
import { getElplanSlotFaults } from './faults'

const NO_STARTS: ReadonlySet<ISODateTime> = new Set()

type ElplanEditor = {
  draft: ElplanDraft
  mode: ElplanSlotUnit
  expandedHourStarts: ReadonlySet<ISODateTime>
  faults: ElplanSlotFaults
  isDirty: boolean
  changedSlotStarts: ReadonlySet<ISODateTime>
  applyEdits: (edits: readonly ElplanSlotEdit[]) => void
  applyTypedEdits: (edits: readonly ElplanSlotEdit[]) => void
  releaseTypedEdits: () => void
  switchMode: (nextMode: ElplanSlotUnit, options?: { acceptValueChanges?: boolean }) => ElplanModeSwitchOutcome
  toggleHour: (hourStart: ISODateTime) => void
  setAllHoursExpanded: (isExpanded: boolean) => void
}

export function useElplanEditor(session: ElplanEditorSession): ElplanEditor {
  const [seed] = useState(session.seed)
  const [draft, setDraft] = useState(seed)
  const [mode, setMode] = useState(seed.resolution)
  const [expandedHourStarts, setExpandedHourStarts] = useState<ReadonlySet<ISODateTime>>(new Set())
  const [typedStarts, setTypedStarts] = useState(NO_STARTS)
  const faults = getElplanSlotFaults(draft, seed, typedStarts)

  function releaseTypedEdits(): void {
    setTypedStarts((previous) => (previous.size === 0 ? previous : NO_STARTS))
  }

  function applyEdits(edits: readonly ElplanSlotEdit[]): void {
    setDraft((previous) => applyElplanSlotEdits(previous, edits))
    releaseTypedEdits()
  }

  function applyTypedEdits(edits: readonly ElplanSlotEdit[]): void {
    setDraft((previous) => applyElplanSlotEdits(previous, edits))
    setTypedStarts(new Set(edits.map(({ start }) => start)))
  }

  function switchMode(
    nextMode: ElplanSlotUnit,
    { acceptValueChanges = false }: { acceptValueChanges?: boolean } = {}
  ): ElplanModeSwitchOutcome {
    releaseTypedEdits()
    if (draft.resolution === 'hour' || nextMode === mode) {
      return { kind: 'unchanged' }
    }
    if (nextMode === 'quarter') {
      setMode('quarter')
      return { kind: 'switched' }
    }

    const flatten = flattenElplanDraftToHours(draft, faults)
    if (flatten.kind === 'refused') {
      setExpandedHourStarts(
        (previous) => new Set([...previous, ...flatten.partlyBlankHourStarts, ...flatten.faultedHourStarts])
      )
      return {
        kind: 'refused',
        partlyBlankHourCount: flatten.partlyBlankHourStarts.length,
        faultedHourCount: flatten.faultedHourStarts.length,
      }
    }
    if (flatten.changedHourCount > 0 && !acceptValueChanges) {
      return { kind: 'needsConfirmation', changedHourCount: flatten.changedHourCount }
    }

    applyEdits(flatten.edits)
    setMode('hour')
    return { kind: 'switched' }
  }

  function toggleHour(hourStart: ISODateTime): void {
    releaseTypedEdits()
    setExpandedHourStarts((previous) => {
      const next = new Set(previous)
      if (!next.delete(hourStart)) {
        next.add(hourStart)
      }
      return next
    })
  }

  function setAllHoursExpanded(isExpanded: boolean): void {
    releaseTypedEdits()
    setExpandedHourStarts(
      isExpanded ? new Set(groupElplanDraftByHour(draft).map(({ hourStart }) => hourStart)) : new Set()
    )
  }

  return {
    draft,
    mode,
    expandedHourStarts,
    faults,
    isDirty: draft.slotStarts.some((start) => draft.textByStart[start] !== seed.textByStart[start]),
    changedSlotStarts: session.target.kind === 'update' ? getElplanChangedStarts(draft, seed) : NO_STARTS,
    applyEdits,
    applyTypedEdits,
    releaseTypedEdits,
    switchMode,
    toggleHour,
    setAllHoursExpanded,
  }
}
